import * as Sentry from '@sentry/react'
import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

function ErrorFallback ({ error, resetError }: { 
  error: unknown; 
  resetError: () => void;
}) {
  const { t } = useTranslation('common')
  const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка'
  
  useEffect(() => {
    document.documentElement.dataset.status = '500'
    return () => {
      delete document.documentElement.dataset.status
    }
  }, [])
  
  return (
    <Container>
      <Wrapper style={{ 
        padding: '100px 0',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: '20px' 
      }}>
        <div role="alert" className={styles.errorContainer}>
          <div className={styles.errorCode}>
            <span>5</span>
            <span>0</span>
            <span>0</span>
          </div>
        
          <h2 className={styles.title}>
          Что-то пошло не так
          </h2>
        
          <p className={styles.description}>
          Произошла непредвиденная ошибка. Мы уже получили уведомление и работаем над исправлением.
          </p>
        
          <details className={styles.details}>
            <summary>
            Показать детали ошибки
            </summary>
            <pre>
              {errorMessage}
            </pre>
          </details>
        
          <div className={styles.actions}>
            <Button 
              onClick={resetError}
              className={styles.button}
              theme="primary"
              size="large"
            >
            Попробовать снова
            </Button>
            <a 
              href="/"
              className={`${styles.button} button-grey button-large`}
            >
              {t('button.main')}
            </a>
          </div>
        </div>
      </Wrapper>
    </Container>
  )
}

interface SentryErrorBoundaryProps {
  children: ReactNode;
  beforeCapture?: (scope: Sentry.Scope, error: unknown, errorInfo: string) => void;
}

export function SentryErrorBoundary ({ 
  children, 
  beforeCapture 
}: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      beforeCapture={(scope, error, errorInfo) => {
        scope.setTag('errorBoundary', true)
        scope.setContext('errorInfo', {
          componentStack: errorInfo,
        })
        
        if (beforeCapture) {
          beforeCapture(scope, error, errorInfo)
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
